import React from 'react';
import '../styles/TermsOfService.css'; // Optional: add your custom CSS for styling

const TermsOfService = () => {
  return (
    <div className="terms-of-service-page">
    <div className="terms-of-use-container">
      <h1>GuideWell Terms of Service</h1>
      <p><strong>Effective Date:</strong> April 1, 2026</p>

      <p>
        Welcome to GuideWell, a property management application operated by DTecX LLC (“DTecX,” “we,” “us,” or “our”).
        By accessing or using GuideWell (the “Service”), you agree to be bound by these Terms of Use (“Terms”).
        If you do not agree to these Terms, do not use the Service.
      </p>

      <section>
        <h2>1. Beta Service Notice</h2>
        <p>
          GuideWell is currently in beta. Features may be incomplete, unstable, or subject to change.
          By using the Service during this period, you acknowledge and accept these limitations.
        </p>
        <ul>
          <li>The Service is provided free of charge during beta; pricing may change upon general release.</li>
          <li>Features and user interface may be added, modified, or removed without notice.</li>
          <li>No guarantees on uptime, data availability, or error-free operation during beta.</li>
          <li>Feedback and usage data may be collected to improve the Service (see Privacy Policy).</li>
          <li>We may end the beta period and transition to a paid model with reasonable notice.</li>
          <li>Data entered during beta will be preserved through general release where possible.</li>
        </ul>
      </section>

      <section>
        <h2>2. About GuideWell</h2>
        <p>
          GuideWell is a real estate property management application designed for individual landlords
          and property management businesses. The Service provides tools for managing properties,
          tenants, leases, financial records, reminders, and AI-assisted features.
        </p>
        <p>
          GuideWell is intended for lawful property management purposes only. It is not a licensed
          legal, financial, accounting, or real estate service. Nothing in the Service constitutes
          professional advice.
        </p>
      </section>

      <section>
        <h2>3. Eligibility</h2>
        <ul>
          <li>You must be at least 18 years old.</li>
          <li>You have the legal capacity to enter into this agreement.</li>
          <li>If using GuideWell on behalf of a business, you are authorized to bind that entity.</li>
        </ul>
      </section>

      <section>
        <h2>4. User Accounts</h2>
        <ul>
          <li>Account creation is required.</li>
          <li>Maintain confidentiality of login credentials.</li>
          <li>Provide accurate information and keep it updated.</li>
          <li>Accounts may not be shared or transferred.</li>
          <li>Account deletion handled as described in Privacy Policy.</li>
          <li>Notify Support@dtecx.net for suspected unauthorized access.</li>
        </ul>
      </section>

      <section>
        <h2>5. Subscriptions and Payment</h2>
        <h3>5.1 Beta Period</h3>
        <p>Free to use; no payment required.</p>

        <h3>5.2 Paid Subscription (Upon General Release)</h3>
        <ul>
          <li>Full-feature access requires a paid subscription.</li>
          <li>Pricing and plans disclosed before any charge.</li>
          <li>Subscriptions auto-renew unless cancelled.</li>
          <li>Cancellation effective at end of billing period.</li>
        </ul>

        <h3>5.3 Refunds</h3>
        <p>Evaluated on a case-by-case basis; not guaranteed except as required by law.</p>
      </section>

      <section>
        <h2>6. Acceptable Use</h2>
        <p>You agree to use GuideWell lawfully and not to:</p>
        <ul>
          <li>Use for illegal, fraudulent, or unauthorized purposes</li>
          <li>Upload unlawful, harmful, abusive, or infringing content</li>
          <li>Reverse-engineer, scrape, or disrupt the Service</li>
          <li>Use bots or automated tools without consent</li>
          <li>Impersonate others or misrepresent affiliations</li>
          <li>Process documents via AI without authorization</li>
          <li>Rely on AI outputs as professional advice</li>
        </ul>
      </section>

      <section>
        <h2>7. AI-Assisted Features</h2>
        <h3>7.1 How It Works</h3>
        <p>Lease documents are processed via Anthropic’s Claude API and not stored permanently by GuideWell.</p>

        <h3>7.2 Your Responsibilities</h3>
        <ul>
          <li>Verify AI-extracted data before saving.</li>
          <li>Upload only legally authorized documents.</li>
          <li>AI outputs may contain errors; review carefully.</li>
        </ul>

        <h3>7.3 No Professional Advice</h3>
        <p>AI-generated content is informational only and not professional advice.</p>

        <h3>7.4 Third-Party AI Terms</h3>
        <p>
          Data submitted to Claude API is subject to Anthropic’s policies.
          DTecX is not responsible for their handling. See anthropic.com/privacy.
        </p>
      </section>

      <section>
        <h2>8. User Data and Content</h2>
        <h3>8.1 Ownership</h3>
        <p>You retain ownership of all content you enter into GuideWell.</p>

        <h3>8.2 License to DTecX</h3>
        <p>
          You grant DTecX a limited license to store, process, and display your content solely to
          operate the Service; this ends on account deletion.
        </p>

        <h3>8.3 Responsibility for Third-Party Data</h3>
        <p>You must handle personal information about others lawfully. DTecX is not responsible.</p>
      </section>

      <section>
        <h2>9. Intellectual Property</h2>
        <p>
          All software, designs, interfaces, trademarks, and content (excluding User Content) are
          DTecX property. You get a limited, personal license to use the Service lawfully.
        </p>
      </section>

      <section>
        <h2>10. Third-Party Services</h2>
        <p>
          GuideWell uses services like Firebase, Claude API, and Apple. Your data may be processed
          by these providers according to their terms. DTecX is not responsible for third-party practices.
        </p>
      </section>

      <section>
        <h2>11. Account Suspension and Termination</h2>
        <p>DTecX may suspend or terminate accounts for violations or security risks. Certain terms survive termination.</p>
      </section>

      <section>
        <h2>12. Disclaimers</h2>
        <p>
          THE SERVICE IS PROVIDED “AS-IS” AND “AS-AVAILABLE” WITHOUT WARRANTIES, INCLUDING
          IMPLIED WARRANTIES OR GUARANTEES OF AI ACCURACY.
        </p>
      </section>

      <section>
        <h2>13. Limitation of Liability</h2>
        <p>
          DTecX is not liable for indirect, incidental, or punitive damages. Maximum liability is the greater of $100 or amount paid in last 12 months.
        </p>
      </section>

      <section>
        <h2>14. Indemnification</h2>
        <p>You agree to indemnify DTecX for claims arising from your use, violations, or unauthorized data.</p>
      </section>

      <section>
        <h2>15. Governing Law and Dispute Resolution</h2>
        <ul>
          <li><strong>Governing Law:</strong> Ohio, USA</li>
          <li><strong>Informal Resolution:</strong> Contact Support@dtecx.net for 30-day resolution period</li>
          <li><strong>Binding Arbitration:</strong> Administered by AAA, individual basis only</li>
          <li><strong>Exceptions:</strong> Small claims or injunctive relief may be pursued in court</li>
        </ul>
      </section>

      <section>
        <h2>16. Changes to These Terms</h2>
        <p>
          We may update Terms. Material changes will be communicated at least 14 days prior.
          Continued use constitutes acceptance.
        </p>
      </section>

      <section>
        <h2>17. General Provisions</h2>
        <ul>
          <li>Entire Agreement: Terms + Privacy Policy form full agreement.</li>
          <li>Severability: Invalid provisions do not affect remaining terms.</li>
          <li>Waiver: Non-enforcement does not waive rights.</li>
          <li>Assignment: You may not transfer rights; DTecX may assign freely</li>
        </ul>
      </section>

      <section>
        <h2>18. Contact Us</h2>
        <p>
          For questions or concerns about these Terms, contact us at:
        </p>
        <p>
          <strong>DTecX LLC</strong><br />
          Email: <a href="mailto:Support@dtecx.net">Support@dtecx.net</a>
        </p>
        <p>We aim to respond to all Terms-related inquiries within 30 days.</p>
      </section>
    </div>
    </div>
  );
};

export default TermsOfService;